/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Typography, Typography2 } from '@coursera/cds-core';

import {
  loadSpecificProctorConfig,
  resetProctorConfigStore,
  updateProctorConfig,
  updateProctorConfigOverride,
} from 'bundles/author-assess-common/actions/ProctorConfigActions';
import ItemTimedReleaseDeadlineEditor from 'bundles/author-assignment-grading/components/common/components/ItemTimedReleaseDeadlineEditor';
import type Team from 'bundles/author-assignment-grading/components/common/types/Team';
import ProgrammingLearnerItemSettingExceptionsProctoringConfigurationAttemptSettingsEditor from 'bundles/author-assignment-grading/components/programming/ProgrammingLearnerItemSettingExceptionsProctoringConfigurationAttemptSettingsEditor';
import NumericInput from 'bundles/author-common/components/NumericInput';
import type Item from 'bundles/author-course/models/Item';
import CourseMaterialUtils from 'bundles/author-course/utils/CourseMaterialUtils';
import { resetSessionModifications } from 'bundles/author-sessions/actions/CourseSessionsActions';
import type SessionItem from 'bundles/author-sessions/models/SessionItem';
import HelpTooltip from 'bundles/authoring/common/components/HelpTooltip';
import PartnerHelpLink from 'bundles/authoring/common/components/PartnerHelpLink';
import type { UserItemGrade } from 'bundles/authoring/common/types/gradebookV2';
import type { ProctorConfig, ProctoredAttemptConfiguration } from 'bundles/authoring/common/types/proctorConfig';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { LoadingSkeleton } from 'bundles/grading';
import {
  clearLearnerItemSchedule,
  getLearnerItemSchedule,
} from 'bundles/student-support-dashboard/actions/LearnerScheduleActions';

import _t from 'i18n!nls/author-assignment-grading';

type PropsFromCaller = {
  currentItem: Item;
  proctorConfigDraft?: ProctorConfig;
  sessionItem?: SessionItem;
  grade?: UserItemGrade;
  userId: string;
  courseId: string;
  isTeamProject?: boolean;

  // for quick grader, we need to get a legacy team object to allow proper refetching
  team?: Team;
  // enable different behavior for different routes when needed
  refetchSessionItem?: boolean;
};

export type Props = PropsFromCaller;

type State = {
  isRefetching: boolean;
};

const styles = {
  root: () => css`
    width: 100%;
    position: relative;

    .learner-item-detailed-view-proctor-settings {
      label {
        margin: var(--cds-spacing-150) 0;
      }

      input {
        height: var(--cds-spacing-600);
        padding: var(--cds-spacing-150);

        &:disabled {
          cursor: not-allowed;
        }
      }

      .time-limit {
        input {
          margin-right: var(--cds-spacing-150);
        }

        p {
          display: inline-block;
          margin-right: var(--cds-spacing-300);
        }
      }

      .disabled-description {
        color: var(--cds-color-grey-600);
      }
    }
  `,
  refetchingSkeleton: () => css`
    margin-top: var(--cds-spacing-300);
    height: 400px;
  `,
};

/**
 *
 * Renders the fields that allow exceptions for a given learner for any settings relevant for the given item
 *
 * @param currentItem The item to override settings for
 * @param proctorConfigDraft The proctor configuration settings for this item/learner.
 * @param sessionItem Session-specific information for the given item
 *
 */
class LearnerItemSettingExceptions extends React.Component<Props, State> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof LearnerItemSettingExceptions.contextTypes>;

  state: State = {
    isRefetching: true,
  };

  componentDidMount() {
    const { refetchSessionItem = false, sessionItem, team, userId } = this.props;

    // see comment under componentWillReceiveProps
    if (refetchSessionItem) {
      // until the BE supports groupPersonalizedSchedules for a team, just load schedule of first team member to display.
      const userIdToLoadScheduleFor = team ? team.members[0] : userId;
      // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      this.loadLearnerItemSchedule(sessionItem, userIdToLoadScheduleFor);

      this.setProctorConfigData();
    }
  }

  /**
   * Only attempt to update fluxible store assets if this is quick grader
   * since legacy grader will always have latest user info from route entry
   * in AssignmentGradingUserSubmissionWrapper, the stores are always updated
   * for legacy grader.
   *
   * This implementation is copied over from AssignmentGradingUserSubmissionWrapper.
   *
   * Since the stores are being updated, the GraderModuleSaveButton will also get
   * the latest props.
   *
   * To be deprecated when Item Setting Exceptions is redesigned.
   */
  componentWillReceiveProps(nextProps: Props) {
    const { refetchSessionItem = false, sessionItem, team, userId } = this.props;

    if (
      refetchSessionItem &&
      ((nextProps.sessionItem && (!sessionItem || sessionItem.id !== nextProps.sessionItem.id)) ||
        userId !== nextProps.userId ||
        !!team?.id !== !!nextProps.team?.id)
    ) {
      this.setState({ isRefetching: true });
      // until the BE supports grouPersonalizedSchedules for a team, just load schedule of first team member to display.
      const userIdToLoadScheduleFor = nextProps.team ? nextProps.team.members[0] : nextProps.userId;

      this.clearSessionModifications(sessionItem?.sessionId, sessionItem?.itemId);
      // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      this.loadLearnerItemSchedule(nextProps.sessionItem, userIdToLoadScheduleFor);

      this.setProctorConfigData();
    }
    this.setState({ isRefetching: false });
  }

  // see comment under componentWillReceiveProps
  componentWillUnmount() {
    const { refetchSessionItem = false, sessionItem } = this.props;
    const { executeAction } = this.context;

    if (refetchSessionItem && sessionItem) {
      const { sessionId, itemId } = sessionItem;

      this.clearSessionModifications(sessionId, itemId);
      executeAction(clearLearnerItemSchedule, { sessionId });
    }
  }

  // Load learner-specific schedule. Quick grader initially loads full session's
  // schedule, so we need to refetch for every quick grader user change
  loadLearnerItemSchedule = (sessionItem: SessionItem | undefined, userId: string | number) => {
    const { executeAction } = this.context;

    if (sessionItem && userId) {
      const { sessionId, groupId } = sessionItem;

      executeAction(getLearnerItemSchedule, {
        userId,
        groupId,
        sessionId,
      });
    }
  };

  setProctorConfigData = () => {
    const { executeAction } = this.context;
    const { currentItem, userId, isTeamProject, sessionItem } = this.props;

    // Note: Quick grader peer-review proctor config setting is not implemented
    if (!isTeamProject) {
      if (currentItem.standardProctorConfigurationId && !currentItem.isPeer) {
        const { standardProctorConfigurationId } = currentItem;
        executeAction(loadSpecificProctorConfig, {
          standardProctorConfigurationId,
          itemId: currentItem.id,
          branchId: sessionItem?.branchId,
          userId,
        });
      } else {
        executeAction(resetProctorConfigStore);
      }
    }
  };

  // Clear lingering session modifications
  clearSessionModifications = (sessionId: SessionItem, itemId: string) => {
    const { executeAction } = this.context;
    executeAction(resetSessionModifications, { sessionId, itemId });
  };

  handleMaxAttemptChange = debounce((newMaxAttempts?: number) => {
    const { executeAction } = this.context;
    const { currentItem } = this.props;

    if (newMaxAttempts !== undefined && newMaxAttempts >= 0) {
      if (currentItem.isPeer) {
        executeAction(updateProctorConfigOverride, {
          maxAttempts: newMaxAttempts,
        });
      } else {
        executeAction(updateProctorConfig, { maxAttempts: newMaxAttempts });
      }
    }
  }, 300);

  handleMaxSubmissionsPerAttemptChange = debounce((newMaxSubmissionsPerAttempt?: number) => {
    const { executeAction } = this.context;
    if (newMaxSubmissionsPerAttempt && newMaxSubmissionsPerAttempt > 0) {
      executeAction(updateProctorConfig, { maxSubmissions: newMaxSubmissionsPerAttempt });
    }
  }, 300);

  handleTimeLimitHourChange = debounce((newHours: number, minutes: number) => {
    const newTimeLimit = CourseMaterialUtils.getTimeInMilliseconds(newHours, minutes);
    const { executeAction } = this.context;
    if (newTimeLimit) {
      executeAction(updateProctorConfig, { timeLimit: newTimeLimit });
    }
  }, 300);

  handleTimeLimitMinuteChange = debounce((newMinutes: number, hours: number) => {
    const newTimeLimit = CourseMaterialUtils.getTimeInMilliseconds(hours, newMinutes);
    const { executeAction } = this.context;
    if (newTimeLimit) {
      executeAction(updateProctorConfig, { timeLimit: newTimeLimit });
    }
  }, 300);

  renderMaxAttemptsEditor = (disabled = false) => {
    const { proctorConfigDraft = { maxAttempts: undefined }, currentItem, isTeamProject } = this.props;
    const maxAttempts = proctorConfigDraft.maxAttempts;
    const initialValueMaxAttempts = maxAttempts !== undefined && maxAttempts >= 0 ? maxAttempts : 1;

    return (
      <div>
        <Typography2 variant="subtitleMedium" component="label">
          {_t('Maximum Number of Attempts')}
          <HelpTooltip message={_t('The maximum number of attempts a learner can have for this assignment.')} />
        </Typography2>
        <div>
          <NumericInput
            disabled={disabled}
            allowDecimal={false}
            allowNegative={false}
            initialValue={initialValueMaxAttempts}
            min={0}
            onChange={this.handleMaxAttemptChange}
          />
          {disabled && (
            <div className="disabled-description">
              {!!isTeamProject && _t('Item setting exceptions are not available for team assignments.')}
              {!isTeamProject &&
                (currentItem.isPeer
                  ? _t('This setting can only be overridden if you configure this item to have a submission limit.')
                  : _t(
                      'This setting can only be overridden if you configure this item to have a time or submission limit.'
                    ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  renderTimeLimitEditor = (disabled = false) => {
    const { proctorConfigDraft, isTeamProject } = this.props;
    let hours = 0;
    let minutes = 0;

    if (!disabled) {
      // There will only be 1 attempt configuration for now.
      const attemptConfiguration = proctorConfigDraft && proctorConfigDraft.attemptConfigurations[0];
      const hasTimeLimit = attemptConfiguration && !!attemptConfiguration.timeLimit;
      ({ hours, minutes } = CourseMaterialUtils.getHoursAndMinutes(
        hasTimeLimit &&
          attemptConfiguration &&
          attemptConfiguration.timeLimit &&
          attemptConfiguration.timeLimit.duration
      ));
    }

    return (
      <div>
        <Typography2 variant="subtitleMedium" component="label">
          {_t('Time Limit Per Attempt')}
          <HelpTooltip
            message={_t('The amount of time learners will have to attempt this assignment once they begin.')}
          />
        </Typography2>
        <div className="time-limit">
          <NumericInput
            disabled={disabled}
            allowDecimal={false}
            allowNegative={false}
            min={0}
            max={99}
            initialValue={hours}
            onChange={(newHours) => this.handleTimeLimitHourChange(newHours, minutes)}
          />
          <Typography2 component="p" variant="bodyPrimary" className="cui-p-margin">
            {_t('Hours')}
          </Typography2>
          <NumericInput
            disabled={disabled}
            allowDecimal={false}
            allowNegative={false}
            min={0}
            max={60}
            initialValue={minutes}
            onChange={(newMinutes) => this.handleTimeLimitMinuteChange(newMinutes, hours)}
          />
          <Typography2 component="p" variant="bodyPrimary" className="cui-p-margin">
            {_t('Minutes')}
          </Typography2>
        </div>
        {disabled && (
          <div className="disabled-description">
            {isTeamProject
              ? _t('Item setting exceptions are not available for team assignments.')
              : _t('This setting can only be overridden if you configure this item to have a time limit.')}
          </div>
        )}
      </div>
    );
  };

  renderMaxSubmissionsEditor = (shouldDisplay: boolean, attempConfiguration: ProctoredAttemptConfiguration) => {
    if (!shouldDisplay) {
      return null;
    }

    return (
      <div>
        <Typography2 variant="subtitleMedium" component="label">
          {_t('Maximum submissions per timed attempt')}
          <HelpTooltip
            message={_t(
              'The maximum number of times a learner can submit during each timed attempt. Can only be set if a time limit is set for this assignment.'
            )}
          />
        </Typography2>
        <div>
          <NumericInput
            allowDecimal={false}
            allowNegative={false}
            initialValue={attempConfiguration.maxSubmissions || 1}
            onChange={this.handleMaxSubmissionsPerAttemptChange}
          />
        </div>
      </div>
    );
  };

  renderProctorConfigurationSettingsEditor() {
    const { proctorConfigDraft, currentItem } = this.props;

    if (
      (!currentItem.isPeer && !currentItem.standardProctorConfigurationId) ||
      !proctorConfigDraft ||
      !proctorConfigDraft.attemptConfigurations
    ) {
      if (currentItem.isStaffGradedProject || currentItem.isQuiz || currentItem.isPeer) {
        // quizzes and projects should show these fields, but they should be disabled if there is no proctor config
        return (
          <div className="learner-item-detailed-view-proctor-settings">
            {this.renderMaxAttemptsEditor(!proctorConfigDraft || !proctorConfigDraft.maxAttempts)}
            {!currentItem.isPeer && this.renderTimeLimitEditor(true)}
          </div>
        );
      } else if (currentItem.isGradedProgramming && 'proctoringConfigurationId' in currentItem.definition) {
        // Note: We are intentionally avoiding proctorConfigDraft because we're reading-from and writing-to different places,
        // so proctorConfigDraft might be empty or have stale data.
        return <ProgrammingLearnerItemSettingExceptionsProctoringConfigurationAttemptSettingsEditor />;
      }

      return null;
    }

    // There will only be 1 attempt configuration for now.
    const attemptConfiguration = proctorConfigDraft.attemptConfigurations[0];

    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    const hasTimeLimit = !!attemptConfiguration.timeLimit;
    const hasMaxSubmissions = Boolean(
      // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      currentItem.isStaffGradedProject && hasTimeLimit && attemptConfiguration.maxSubmissions
    );

    return (
      <div className="learner-item-detailed-view-proctor-settings">
        {this.renderMaxAttemptsEditor(
          proctorConfigDraft.maxAttempts === undefined && !hasTimeLimit && !hasMaxSubmissions
        )}
        {!currentItem.isPeer && this.renderTimeLimitEditor(!hasTimeLimit)}
        {/*
         // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess */}
        {!currentItem.isPeer && this.renderMaxSubmissionsEditor(hasMaxSubmissions, attemptConfiguration)}
      </div>
    );
  }

  renderHeader() {
    return (
      <div className="properties-header">
        <Typography variant="h1semibold" component="h2" className="m-b-1">
          {_t('Item Setting Exceptions')}
        </Typography>
        <Typography2 component="p" variant="bodyPrimary" className="cui-p-margin">
          <FormattedMessage
            message={_t(
              'Item setting exceptions allow you to make accommodations for individual learners on a particular assignment. {learnMoreLink}'
            )}
            learnMoreLink={<PartnerHelpLink />}
          />
        </Typography2>
      </div>
    );
  }

  render() {
    const { refetchSessionItem = false, sessionItem, userId } = this.props;
    const { isRefetching } = this.state;
    const parentClassName = 'rc-LearnerItemSettingExceptions';

    return (
      <div data-testid="legacy-learner-item-setting-exceptions" className={parentClassName} css={styles.root}>
        <div className="content-container">
          <div className="content">
            {this.renderHeader()}
            {refetchSessionItem && isRefetching ? (
              <LoadingSkeleton cssOverride={styles.refetchingSkeleton} />
            ) : (
              <React.Fragment>
                <ItemTimedReleaseDeadlineEditor item={sessionItem} userId={userId} parentEl={`.${parentClassName}`} />
                {this.renderProctorConfigurationSettingsEditor()}
              </React.Fragment>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default LearnerItemSettingExceptions;
